import type { AIAnalysisRequestParams } from '@/api/precision-new/analysis';
import type { AIAnalysisResult, AnalysisStage, AnalysisStatus } from '@/types/precision-new/ai-analysis';
import { defineStore } from 'pinia';

export interface AIAnalysisTask {
  taskId: string;
  planId: number;
  planName: string;
  projectId: number;
  projectName: string;
  version?: string;
  status: AnalysisStatus;
  progress: number;
  stage: AnalysisStage;
  message: string;
  error?: string;
  startTime: number;
  endTime?: number;
  minimized: boolean;
  result?: AIAnalysisResult;
  // 分析参数，用于重试
  analysisParams?: AIAnalysisRequestParams;
}

export const useAIAnalysisTasksStore = defineStore('aiAnalysisTasks', {
  state: () => ({
    tasks: new Map<string, AIAnalysisTask>(),
    activeTaskId: null as string | null,
    panelVisible: false,
    panelMinimized: true,
    // 添加版本号以触发响应式更新
    _version: 0,
  }),

  getters: {
    /**
     * 获取所有活跃任务（正在运行或出错需要处理）
     */
    activeTasks: (state) => {
      return Array.from(state.tasks.values()).filter((task) => task.status === 'analyzing' || task.status === 'error');
    },

    /**
     * 获取需要关注的任务数量（正在运行 + 出错）
     */
    activeTaskCount: (state) => {
      return Array.from(state.tasks.values()).filter((task) => task.status === 'analyzing' || task.status === 'error')
        .length;
    },

    /**
     * 获取出错的任务数量
     */
    errorTaskCount: (state) => {
      return Array.from(state.tasks.values()).filter((task) => task.status === 'error').length;
    },

    /**
     * 获取任务状态的字符串表示（用于触发响应式更新）
     */
    tasksStateKey: (state) => {
      const tasks = Array.from(state.tasks.values());
      return `${state._version}-${tasks.map((t) => `${t.taskId}:${t.status}:${t.progress}`).join('|')}`;
    },

    /**
     * 根据计划ID获取任务
     */
    getTaskByPlanId: (state) => (planId: number) => {
      return Array.from(state.tasks.values()).find((task) => task.planId === planId);
    },

    /**
     * 获取当前活跃的任务
     */
    activeTask: (state) => {
      return state.activeTaskId ? state.tasks.get(state.activeTaskId) : null;
    },

    /**
     * 获取所有任务列表（按开始时间倒序）
     */
    allTasks: (state) => {
      return Array.from(state.tasks.values()).sort((a, b) => b.startTime - a.startTime);
    },

    /**
     * 获取已完成的任务列表
     */
    completedTasks: (state) => {
      return Array.from(state.tasks.values())
        .filter((task) => task.status === 'completed')
        .sort((a, b) => (b.endTime || 0) - (a.endTime || 0));
    },
  },

  actions: {
    /**
     * 创建新任务
     */
    createTask(
      params: Omit<AIAnalysisTask, 'taskId' | 'startTime' | 'minimized' | 'status' | 'progress' | 'stage' | 'message'>,
    ): string {
      const taskId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const task: AIAnalysisTask = {
        ...params,
        taskId,
        status: 'idle',
        progress: 0,
        stage: 'data_collection',
        message: '等待开始分析...',
        startTime: Date.now(),
        minimized: false,
      };

      this.tasks.set(taskId, task);
      this.activeTaskId = taskId;
      this.panelVisible = true;
      this.panelMinimized = false;
      this._version++; // 触发响应式更新

      // 限制任务数量，保留最近100个任务
      if (this.tasks.size > 100) {
        const sortedTasks = Array.from(this.tasks.entries()).sort((a, b) => a[1].startTime - b[1].startTime);
        const oldestTaskId = sortedTasks[0][0];
        this.tasks.delete(oldestTaskId);
        this._version++; // 触发响应式更新
      }

      return taskId;
    },

    /**
     * 更新任务状态
     */
    updateTask(taskId: string, updates: Partial<AIAnalysisTask>) {
      const task = this.tasks.get(taskId);
      if (task) {
        this.tasks.set(taskId, { ...task, ...updates });
        this._version++; // 触发响应式更新

        // 如果任务完成或出错，记录结束时间
        if ((updates.status === 'completed' || updates.status === 'error') && !task.endTime) {
          this.tasks.set(taskId, {
            ...task,
            ...updates,
            endTime: Date.now(),
          });
          this._version++; // 触发响应式更新
        }
      }
    },

    /**
     * 切换任务详情显示
     */
    showTaskDetail(taskId: string) {
      const task = this.tasks.get(taskId);
      if (task) {
        this.activeTaskId = taskId;
        this.panelMinimized = false;
        this.panelVisible = true;
      }
    },

    /**
     * 最小化面板
     */
    minimizePanel() {
      this.panelMinimized = true;
    },

    /**
     * 最大化面板
     */
    maximizePanel() {
      this.panelMinimized = false;
      this.panelVisible = true;
    },

    /**
     * 关闭面板（隐藏）
     */
    closePanel() {
      this.panelVisible = false;
    },

    /**
     * 完全删除任务
     */
    removeTask(taskId: string) {
      this.tasks.delete(taskId);
      this._version++; // 触发响应式更新
      if (this.activeTaskId === taskId) {
        this.activeTaskId = null;
      }

      // 如果没有任务了，关闭面板
      if (this.tasks.size === 0) {
        this.panelVisible = false;
      }
    },

    /**
     * 回到任务列表
     */
    backToList() {
      this.activeTaskId = null;
    },

    /**
     * 清理已完成的任务
     */
    clearCompletedTasks() {
      const completedTaskIds = Array.from(this.tasks.entries())
        .filter(([, task]) => task.status === 'completed' || task.status === 'error')
        .map(([id]) => id);

      completedTaskIds.forEach((id) => {
        if (this.activeTaskId !== id) {
          this.tasks.delete(id);
        }
      });

      if (completedTaskIds.length > 0) {
        this._version++; // 触发响应式更新
      }
    },

    /**
     * 取消任务
     */
    cancelTask(taskId: string) {
      this.updateTask(taskId, {
        status: 'idle',
        message: '已取消',
      });
    },

    /**
     * 重试任务
     */
    retryTask(taskId: string) {
      const task = this.tasks.get(taskId);
      if (task && task.analysisParams) {
        this.updateTask(taskId, {
          status: 'analyzing',
          progress: 0,
          stage: 'data_collection',
          message: '正在重新开始分析...',
          error: undefined,
          startTime: Date.now(),
          endTime: undefined,
        });
        return task.analysisParams;
      }
      return null;
    },
  },
});
